import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';

const STORE_URL = window.location.hostname === 'store.oplogman.com' ? '/' : 'https://store.oplogman.com';

export default function PrivacyPage() {
  useEffect(() => { document.title = "Privacy & Terms | Opulence Logistics Management"; }, []);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      {/* Header */}
      <header className="bg-slate-900 text-white py-6 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/contact" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-royal-blue-500 to-royal-blue-700 flex items-center justify-center">
                <span className="text-white font-bold">O</span>
              </div>
              <div>
                <h1 className="font-cabinet font-bold text-lg leading-tight">OpLogMan</h1>
                <p className="text-xs text-slate-400">Opulence Logistics Management Corp.</p>
              </div>
            </Link>
          </div>
          <div className="flex gap-4 text-sm text-slate-400">
            <a href={STORE_URL} className="hover:text-white transition-colors">Store</a>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/contact" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-royal-blue-600 transition-colors mb-8" data-testid="back-to-contact">
          <ArrowLeft size={16} /> Back to Contact
        </Link>

        {/* Privacy Policy */}
        <Card className="mb-8 border-slate-200 shadow-sm" data-testid="privacy-policy-section">
          <CardContent className="p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-lg bg-royal-blue-100">
                <Shield size={22} className="text-royal-blue-700" />
              </div>
              <h2 className="font-cabinet text-2xl font-bold text-slate-900">Privacy Policy</h2>
            </div>

            <p className="text-slate-600 mb-8 leading-relaxed">
              Opulence Logistics Management Corp. ("Company," "we," "our," or "us") respects your privacy and is committed to protecting your personal information.
            </p>

            <div className="space-y-8">
              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">1. Information We Collect</h3>
                <p className="text-slate-600 mb-3">We may collect the following information:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
                  <li>Name, email address, and phone number</li>
                  <li>Business information provided by clients</li>
                  <li>Payment and billing details (processed securely through third-party providers)</li>
                  <li>Website usage data (if applicable)</li>
                </ul>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">2. How We Use Your Information</h3>
                <p className="text-slate-600 mb-3">We use your information to:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
                  <li>Provide consulting, project management, and related services</li>
                  <li>Communicate with clients and respond to inquiries</li>
                  <li>Process payments and manage transactions</li>
                  <li>Improve our services and client experience</li>
                </ul>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">3. Information Sharing</h3>
                <p className="text-slate-600 mb-3">We do not sell or rent your personal information. We may share information:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
                  <li>With trusted service providers (e.g., payment processors)</li>
                  <li>When required by law or legal process</li>
                </ul>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">4. Data Security</h3>
                <p className="text-slate-600">We implement reasonable administrative and technical safeguards to protect your information. However, no system is 100% secure.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">5. Your Rights</h3>
                <p className="text-slate-600">You may request access to, correction of, or deletion of your personal information by contacting us.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">6. Contact Information</h3>
                <div className="bg-slate-50 rounded-xl p-5 space-y-2">
                  <p className="font-medium text-slate-800">Opulence Logistics Management Corp.</p>
                  <p className="text-slate-600 flex items-center gap-2"><MapPin size={15} className="text-slate-400 flex-shrink-0" /> 1250 W Sam Houston Pkwy S, Suite 302, Houston, TX 77042</p>
                  <p className="text-slate-600 flex items-center gap-2"><Mail size={15} className="text-slate-400" /> Brandon@oplogman.com</p>
                  <p className="text-slate-600 flex items-center gap-2"><Phone size={15} className="text-slate-400" /> 281-938-9601</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <Card className="border-slate-200 shadow-sm" data-testid="terms-section">
          <CardContent className="p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-lg bg-amber-100">
                <FileText size={22} className="text-amber-700" />
              </div>
              <h2 className="font-cabinet text-2xl font-bold text-slate-900">Terms and Conditions</h2>
            </div>

            <div className="space-y-8">
              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">1. Acceptance of Terms</h3>
                <p className="text-slate-600">By engaging with Opulence Logistics Management Corp., you agree to these Terms and Conditions.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">2. Services</h3>
                <p className="text-slate-600">We provide project management, business consulting, event production, and logistics services. Scope of services will be defined in written agreements or proposals.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">3. Payments & Fees</h3>
                <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
                  <li>All fees must be paid as agreed upon in contracts or invoices</li>
                  <li>Payments may be required upfront or on a scheduled basis</li>
                  <li>Late payments may result in service delays or suspension</li>
                </ul>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">4. No Guarantees</h3>
                <p className="text-slate-600">While we strive to deliver high-quality results, we do not guarantee specific financial or business outcomes.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">5. Client Responsibilities</h3>
                <p className="text-slate-600 mb-3">Clients agree to:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
                  <li>Provide accurate and timely information</li>
                  <li>Respond promptly to requests</li>
                  <li>Fulfill agreed-upon obligations</li>
                </ul>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">6. Intellectual Property</h3>
                <p className="text-slate-600">All materials, strategies, and deliverables provided remain the intellectual property of Opulence Logistics Management Corp. unless otherwise agreed in writing.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">7. Limitation of Liability</h3>
                <p className="text-slate-600">We are not liable for indirect, incidental, or consequential damages arising from the use of our services.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">8. Termination</h3>
                <p className="text-slate-600">We reserve the right to terminate services if terms are violated, including non-payment or breach of agreement.</p>
              </section>

              <section>
                <h3 className="font-cabinet text-lg font-semibold text-slate-800 mb-3">9. Governing Law</h3>
                <p className="text-slate-600">These terms are governed by the laws of the State of Texas, United States.</p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-royal-blue-500 to-royal-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">O</span>
              </div>
              <span className="font-cabinet font-bold text-sm">Opulence Logistics Management Corp.</span>
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href={STORE_URL} className="hover:text-white transition-colors">Store</a>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
